import { Link } from 'react-router-dom';
import { ArrowRightIcon, GiftIcon } from 'lucide-react';
import { ProductCard } from '../shared/ProductCard';
import { HeadingText } from '../shared/HeadingText';
import { Rail } from '../shared/Rail';
import { usePages, useSite } from '../../contexts/SiteContext';
import { euro } from '../../core/format/currency';
import { fill } from '../../utils/copy';
import { quietLink, sectionHeading } from '../../ui/sections';
import type { Product } from '../../core/types/catalog';
type Props = {
  products: Product[];
  total: number;
};

/**
 * "Liever met één product beginnen?": de lage drempel na het complete plan. Bestsellers en
 * salonfavorieten op de rail; de kaarten komen kort na elkaar binnen.
 *
 * Daaronder de cadeaustaffel. Die geldt voor elke bestelling, ook voor één product, dus hoort
 * hij hier: wie klein begint, ziet meteen wat er bij een iets grotere bestelling bij komt.
 */
export function HomeShelf({
  products,
  total
}: Props) {
  const pages = usePages();
  const {
    giftTiers
  } = useSite();
  const copy = pages.home.bestsellers;
  return <section className="bg-sand py-20 sm:py-24 lg:py-32" aria-labelledby="home-bestsellers" data-id="element-7482" data-mp="245qczs0oblgb">
      <div className="mx-auto max-w-content px-6 lg:px-10" data-id="element-7483" data-mp="204myp9y9xxh7">
        <div className="flex flex-wrap items-end justify-between gap-6" data-id="element-7484" data-mp="1v63af2w7k9lo">
          <div className="max-w-2xl" data-id="element-7485" data-mp="1q8ted4ebczov">
            <h2 id="home-bestsellers" className={`${sectionHeading} text-ink`} data-id="element-7486" data-mp="1q7rzeou6ft1v">
              <HeadingText heading={copy.heading} data-id="element-7487" data-mp="1la8krntkhe9e" />
            </h2>
            {copy.body && <p className="mt-5 max-w-xl text-[17px] leading-relaxed text-muted" data-id="element-7488" data-mp="1gi8gxkadm17j">{fill(copy.body)}</p>}
          </div>
          {copy.cta && <Link to="/webshop" className={`${quietLink} text-ink`} data-id="element-7489" data-mp="1ch51v005k97d">
              {fill(copy.cta, {
            count: total
          })}
              <ArrowRightIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-7490" data-mp="17fz8czyo4hws" />
            </Link>}
        </div>

        <Rail id="home-shelf" label="Salonfavorieten" className="mt-10 lg:mt-12" stagger items={products} itemKey={p => p.id} itemClassName="w-[68%] sm:w-[40%] lg:w-[calc((100%-4.5rem)/3.5)]" renderItem={p => <div className="h-full w-full transition-[transform,box-shadow] duration-150 ease-soft [@media(hover:hover)]:hover:-translate-y-1 [@media(hover:hover)]:hover:shadow-lift" data-id="element-7492" data-mp="evu6bxp8qv3x">
              <ProductCard product={p} data-id="element-7493" data-mp="16mrv4qohlvk4" />
            </div>} data-id="element-7491" data-mp="xjcoade61uvt" />

        {/* De staffel als één rij: bedrag en cadeau naast elkaar (telefoon: onder elkaar). */}
        {copy.giftsTitle && giftTiers.length > 0 && <div className="mt-14 border-t border-ink/15 pt-8 lg:mt-16" data-id="element-7494" data-mp="xt541dv85gh9">
            <h3 className="flex items-center gap-2.5 text-[15px] text-ink" data-id="element-7495" data-mp="11v9sly6tfmx1">
              <GiftIcon className="h-4 w-4 text-clay" strokeWidth={1.5} aria-hidden="true" data-id="element-7496" data-mp="11tf98ob702v2" />
              {fill(copy.giftsTitle)}
            </h3>
            <ul aria-label="Cadeaus bij je bestelling" className={`mt-5 grid gap-px bg-ink/10 sm:grid-cols-2 ${giftTiers.length >= 3 ? 'lg:grid-cols-3' : ''} ${giftTiers.length >= 4 ? 'xl:grid-cols-4' : ''}`} data-id="element-7497" data-mp="1ud48ih2i4ki3">
              {giftTiers.map(tier => <li key={tier.threshold} className="flex gap-4 bg-sand py-4 sm:px-5 sm:first:pl-0" data-id="element-7498" data-mp="3or9fdj72uvtf">
                  <span className="w-16 shrink-0 font-display text-xl text-clay" data-id="element-7499" data-mp="3ju9b9dtsg34y">
                    <span className="sr-only" data-id="element-7500" data-mp="2w33xr5bblmxt">Vanaf </span>
                    {euro(tier.threshold)}
                  </span>
                  <span className="text-[14px] leading-snug text-muted" data-id="element-7501" data-mp="2r5u75mc7358e">
                    <span className="block text-ink" data-id="element-7502" data-mp="362360ms6bgka">{tier.title}</span>
                    {tier.description}
                  </span>
                </li>)}
            </ul>
          </div>}
      </div>
    </section>;
}